import styled, { DefaultTheme } from "styled-components";
import { Card, Row, Modal, Badge, Button, Space, Descriptions, Alert, Typography, Divider, Avatar } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
const { Title } = Typography;

// Declare module augmentation for styled-components theme
declare module "styled-components" {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

// Theme helper function
const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === "dark" ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px 16px 16px 0px;
  background: ${(props) => getThemeValue(props, "#171923", "transparent")};
  color: ${(props) => getThemeValue(props, "#fff", "inherit")};
`;

export const StyledCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, "#718096", "#DCDCDC")};
  margin-bottom: 24px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};

  .ant-card-body {
    padding: 12px 12px 12px 12px;
    border-radius: 12px;
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  }
`;

export const StatsRow = styled(Row)`
  margin-bottom: 24px;
  
  @media (min-width: 992px) {
    .ant-col {
      flex: 1 1 0% !important;
      max-width: none !important;
    }
  }
`;

export const StatCard = styled(Card)<{ cardType?: string }>`
  --title-color: ${(props) => {
    const type = props.cardType || 'default';
    const colors: Record<string, string> = {
      total: '#096dd9',
      needsResponse: '#cf1322',
      underReview: '#13c2c2',
      won: '#389e0d',
      lost: '#d46b08',
      default: '#8c8c8c',
    };
    return colors[type] || colors.default;
  }};
  
  border-radius: 12px;
  width: 100%;
  height: 100%;
  border: 1px solid ${(props) => {
    const isDark = props.theme?.mode === "dark";
    const type = props.cardType || 'default';
    if (isDark) {
      const colors: Record<string, string> = {
        total: '#111a2c',
        needsResponse: '#2b1d0f',
        underReview: '#0f2b2b',
        won: '#162312',
        lost: '#2b1d0f',
        default: '#262626',
      };
      return colors[type] || colors.default;
    }
    const colors: Record<string, string> = {
      total: '#e6f4ff',
      needsResponse: '#fff1f0',
      underReview: '#e6fffb',
      won: '#f6ffed',
      lost: '#fff7e6',
      default: '#f0f0f0',
    };
    return colors[type] || colors.default;
  }};
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.08)")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) => {
      const type = props.cardType || 'default';
      const colors: Record<string, string> = {
        total: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
        needsResponse: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
        underReview: 'linear-gradient(135deg, #36cfc9 0%, #13c2c2 100%)',
        won: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
        lost: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
        default: 'linear-gradient(135deg, #8c8c8c 0%, #595959 100%)',
      };
      return colors[type] || colors.default;
    }};
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${(props) => getThemeValue(props, "0 8px 24px rgba(0, 0, 0, 0.5)", "0 8px 24px rgba(0, 0, 0, 0.12)")};
    border-color: ${(props) => {
      const isDark = props.theme?.mode === "dark";
      const type = props.cardType || 'default';
      if (isDark) {
        const colors: Record<string, string> = {
          total: '#1a3a5c',
          needsResponse: '#4a2e0f',
          underReview: '#1a3a3a',
          won: '#274916',
          lost: '#4a2e0f',
          default: '#434343',
        };
        return colors[type] || colors.default;
      }
      const colors: Record<string, string> = {
        total: '#91d5ff',
        needsResponse: '#ffccc7',
        underReview: '#87e8de',
        won: '#d9f7be',
        lost: '#ffe7ba',
        default: '#d9d9d9',
      };
      return colors[type] || colors.default;
    }};
  }

  .ant-card-body {
    padding: 16px 18px;
  }

  .ant-statistic {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .ant-statistic-title {
    font-size: 13px;
    color: var(--title-color) !important;
    font-weight: 600;
    margin-bottom: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-statistic-content {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    flex-wrap: wrap;
  }

  .ant-statistic-content-value {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.2;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${(props) => getThemeValue(props, "#fff", "inherit")};
  }

  .ant-statistic-content-prefix {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${(props) => {
      const type = props.cardType || 'default';
      const colors: Record<string, string> = {
        total: 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
        needsResponse: 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
        underReview: 'linear-gradient(135deg, #e6fffb 0%, #b5f5ec 100%)',
        won: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
        lost: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
        default: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
      };
      return colors[type] || colors.default;
    }};
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    position: relative;
    
    .anticon {
      font-size: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      line-height: 1;
      margin: 0;
      padding: 0;
    }
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  color: ${(props) => getThemeValue(props, "#fff", "inherit")};
`;

export const TopControlBar = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#fff")};
  border-bottom: 1px solid ${(props) => getThemeValue(props, "#718096", "#f0f0f0")};
  gap: 16px;
  flex-wrap: wrap;
`;

export const StyledModal = styled(Modal)`
  z-index: 2000 !important;

  .ant-modal-mask {
    z-index: 2000 !important;
    background-color: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
  }

  .ant-modal-wrap {
    z-index: 2000 !important;
  }

  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: ${(props) => getThemeValue(props, "0 20px 60px rgba(0, 0, 0, 0.5)", "0 20px 60px rgba(0, 0, 0, 0.15)")};
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  }

  .ant-modal-header {
    border-bottom: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
    padding: 20px 24px;
    background: ${(props) => getThemeValue(props, "#1a202c", "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)")};
    border-radius: 16px 16px 0 0;

    .ant-modal-title {
      font-size: 20px;
      font-weight: 600;
      color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
    }
  }

  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
    padding: 24px;
    background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")};
  }

  .ant-modal-footer {
    border-top: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
    padding-top: 10px;
    border-radius: 0 0 16px 16px;
  }

  .ant-modal-close {
    top: 20px;
    right: 24px;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    transition: all 0.2s;
    color: ${(props) => getThemeValue(props, "#fff", "inherit")};

    &:hover {
      background-color: ${(props) => getThemeValue(props, "#4a5568", "#f5f5f5")};
    }
  }
`;

export const StyledBadge = styled(Badge)`
  .ant-badge-status-dot {
    width: 8px;
    height: 8px;
  }
`;

export const InfoSection = styled.div`
  margin-bottom: 24px;
`;

export const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #8c8c8c;
  margin-bottom: 8px;
`;

export const InfoValue = styled.div`
  font-size: 14px;
  color: #262626;
`;

export const NotesCard = styled(Card)`
  margin-top: 16px;
  border-radius: 12px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")};
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
  box-shadow: ${(props) => getThemeValue(props, "0 1px 4px rgba(0, 0, 0, 0.3)", "0 1px 4px rgba(0, 0, 0, 0.04)")};

  .ant-card-body {
    padding: 16px 20px;
    background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")};
  }
`;

export const ActionIconButton = styled.div<{ iconColor?: string }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#eee")};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  background: ${(props) => getThemeValue(props, "transparent", "inherit")};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) => {
      const iconColor = props.iconColor || "#2D6CCA";
      if (iconColor === "#f5222d") {
        return getThemeValue(props, "rgba(245, 34, 45, 0.2)", "rgba(245, 34, 45, 0.1)");
      }
      return getThemeValue(props, "rgba(45, 108, 202, 0.2)", "rgba(45, 108, 202, 0.1)");
    }} !important;

    svg {
      color: ${(props) => props.iconColor || "#2D6CCA"} !important;
    }
  }
`;

export const CustomerName = styled.div`
  font-weight: 500;
  color: ${(props) => getThemeValue(props, "#fff", "#121212")};
`;

export const CustomerEmail = styled.div`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, "#8c8c8c", "#8c8c8c")};
`;

export const TableText = styled.span`
  color: ${(props) => getThemeValue(props, "#fff", "#121212")};
`;

export const ClockIconWrapper = styled.div<{ isUrgent?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.isUrgent ? "#ff4d4f" : "#8c8c8c")};
  line-height: 1;
  vertical-align: middle;
`;

export const DueByText = styled.span<{ isUrgent?: boolean }>`
  color: ${(props) => {
    if (props.isUrgent) return "#ff4d4f";
    return getThemeValue(props, "#fff", "#121212");
  }};
  font-weight: ${(props) => (props.isUrgent ? 600 : 400)};
  display: inline-flex;
  align-items: center;
  line-height: 1;
  vertical-align: middle;
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

export const CloseButton = styled(Button)`
  border: 0 !important;
  color: #222222 !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  height: 38px !important;
  font-size: 16px !important;
  padding: 8px 16px !important;
  background-color: #EDEDED !important;

  &:hover {
    color: #222222 !important;
    background-color: #DEDEDE !important;
  }
`;

export const SubmitButton = styled(Button)`
  background-color: #2D6CCA;
  font-weight: 500 !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  height: 38px !important;

  &:hover {
    background-color: #1e4d8f !important;
  }
`;

export const UrgentAlert = styled(Alert)`
  margin-bottom: 24px;
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 12px rgba(245, 34, 45, 0.15);
  
  .ant-alert-message {
    font-weight: 600;
    font-size: 15px;
  }
`;

export const HeaderSpace = styled(Space)`
  margin-bottom: 24px;
  padding: 20px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  border-radius: 12px;
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.04)")};
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
`;

export const TitleNoMargin = styled(Title)`
  margin: 0 !important;
`;

export const DescriptionsWithMargin = styled(Descriptions)`
  margin-bottom: 24px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  padding: 20px;
  border-radius: 12px;
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.04)")};
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};

  .ant-descriptions-item-label {
    font-weight: 600;
    color: ${(props) => getThemeValue(props, "#bfbfbf", "#595959")};
    background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")} !important;
  }

  .ant-descriptions-item-content {
    color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
  }
`;

export const SectionTitle = styled(Title)`
  margin-bottom: 16px !important;
  color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")} !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #2D6CCA 0%, #1e4d8f 100%);
    border-radius: 2px;
    display: inline-block;
  }
`;

export const OrderCard = styled(Card)`
  margin-bottom: 0;
  border-radius: 12px;
  box-shadow: none;
  border: none;
  background: transparent;

  .ant-card-body {
    padding: 0;
  }
`;

export const OrderDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const OrderDetailRow = styled.div<{ isTotal?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.isTotal ? '16px 0' : '12px 0'};
  border-bottom: ${(props) => {
    const borderColor = getThemeValue(props, "#434343", props.isTotal ? "#f0f0f0" : "#f5f5f5");
    return props.isTotal ? `2px solid ${borderColor}` : `1px solid ${borderColor}`;
  }};
  
  &:last-child {
    border-bottom: none;
  }
`;

export const OrderDetailLabel = styled.span<{ isTotal?: boolean }>`
  font-size: ${props => props.isTotal ? '15px' : '14px'};
  font-weight: ${props => props.isTotal ? '600' : '500'};
  color: ${(props) => getThemeValue(props, "#bfbfbf", "#595959")};
`;

export const OrderDetailValue = styled.span<{ isTotal?: boolean }>`
  font-size: ${props => props.isTotal ? '18px' : '14px'};
  font-weight: ${props => props.isTotal ? '700' : '400'};
  color: ${(props) => {
    if (props.isTotal) {
      return getThemeValue(props, "#fff", "#1a1a1a");
    }
    return getThemeValue(props, "#fff", "#262626");
  }};
`;

export const OrderItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0;
`;

export const OrderItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
`;

export const OrderItemInfo = styled.span`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, "#fff", "#262626")};
  font-weight: 500;
`;

export const OrderItemPrice = styled.span`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
  font-weight: 600;
`;

export const NotesSection = styled.div`
  margin-bottom: 24px;
`;

export const NotesHeader = styled.div`
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const EditButton = styled.button`
  padding: 5px 10px;
  border: 1px solid #EEEEEE;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #222222;
  font-weight: 500;

  &:hover {
    color: #222222;
    background-color: #EEEEEE;
  }
`;

export const NotesText = styled.div`
  white-space: pre-wrap;
  color: ${(props) => getThemeValue(props, "#fff", "inherit")};
`;

export const ItemWrapper = styled.div`
  margin-bottom: 8px;
`;

export const ClockIcon = styled(ClockCircleOutlined)<{ isUrgent?: boolean }>`
  color: ${(props) => (props.isUrgent ? "#ff4d4f" : "#8c8c8c")} !important;
`;

export const DueByTextStyled = styled.span<{ isUrgent?: boolean }>`
  color: ${(props) => (props.isUrgent ? "#ff4d4f" : "#262626")} !important;
  font-weight: ${(props) => (props.isUrgent ? 600 : 400)} !important;
`;

export const SectionWrapper = styled.div`
  margin-bottom: 24px;
`;

export const ModernDivider = styled(Divider)`
  margin: 24px 0;
  border-color: #f0f0f0;
  
  &::before,
  &::after {
    border-color: #f0f0f0;
  }
`;

export const ModalContentWrapper = styled.div`
  background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")};
  min-height: 100%;
`;

export const SectionCard = styled.div`
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  padding: 20px;
  border-radius: 12px;
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.04)")};
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
  margin-bottom: 24px;
`;

export const CustomerAvatar = styled(Avatar)`
  background: linear-gradient(135deg, #2D6CCA 0%, #1e4d8f 100%);
  box-shadow: 0 4px 12px rgba(45, 108, 202, 0.25);
  font-weight: 600;
  font-size: 24px;
`;

export const CustomerInfo = styled.div`
  .ant-typography {
    margin-bottom: 4px;
  }
`;

export const StyledDivider = styled(Divider)`
  margin: 24px 0;
  border-color: ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
`;

